import {
  Box,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Modal,
  Button,
  TableSortLabel,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import React, { useState } from "react";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { deleteSteelPerTray } from "../../api/steelpertray/Api.service";
import { Order, getComparator, stableSort } from "./SteelPerTrayTable";
import { visuallyHidden } from "@mui/utils";
import { reformatDates } from "../../utils/reformatDates";
import titleCase from "../../utils/titleCase";

const tableCells: any = [
  {
    id: "code",
    label: "CODE",
  },
  {
    id: "name",
    label: "PRODUCT",
  },
  {
    id: "grade",
    label: "GRADE",
  },
  {
    id: "brand",
    label: "BRAND",
  },
  {
    id: "manufacturer",
    label: "MANUFACTURER",
  },
  {
    id: "thickness",
    label: "THICKNESS",
  },
  {
    id: "width",
    label: "WIDTH",
  },
  {
    id: "length",
    label: "LENGTH",
  },
  {
    id: "quantity",
    label: "QTY",
  },
];

function EnhancedTableHead(props: any) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {tableCells.map((headCell: any, index: number) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              fontWeight: "600",
              backgroundColor: "#f5f6fa",
              border: "1px solid #e0e0e07a",
              borderBottom: "0",
              textAlign: "center",
            }}
          >
            {headCell.label && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
                disabled={headCell.id === "action"}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function SteelPerTrayRow(props: any): JSX.Element {
  const { row, index, refreshSteelPerTray, onEdit } = props;
  const [open, setOpen] = useState(false);
  const dispatch: AppDispatch = useDispatch();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<any>("code");

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = React.useMemo(
    () => stableSort(row?.steelpertrays, getComparator(order, orderBy)),
    [order, orderBy, row?.steelpertrays]
  );
  const collapsedStyles = {
    border: "1px solid #e0e0e07a",
    width: "100px !important",
    textAlign: "center",
  };

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "20px",
  };

  const synopsisStyles = {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    maxWidth: "150px",
    textAlign: "center",
  };

  const tableCellStyle = {
    // background: "red",
    borderRight: "1px solid #e0e0e07a",
    borderBottom: "1px solid #e0e0e07a",
  };

  const handleClose = () => {
    setOpenDeleteModal(false);
  };

  const handlClick = () => {
    setOpenDeleteModal(true);
  };

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        key={`uncollapsed-row-${index}`}
      >
        <TableCell sx={tableCellStyle}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell
          component="th"
          scope="row"
          title={row?.name}
          sx={{ ...tableCellStyle, ...synopsisStyles }}
        >
          {row?.name}
        </TableCell>
        <TableCell
          component="th"
          scope="row"
          title={row?.tray?.name}
          sx={{ ...tableCellStyle, ...synopsisStyles }}
        >
          {row?.tray?.name}
        </TableCell>
        <TableCell
          align="left"
          title={reformatDates(row?.effectiveStartDate) ?? "-"}
          sx={{ ...tableCellStyle, ...synopsisStyles }}
        >
          {reformatDates(row?.effectiveStartDate) ?? "-"}
        </TableCell>
        <TableCell
          align="left"
          title={reformatDates(row?.effectiveEndDate ?? "-")}
          sx={{ ...tableCellStyle, ...synopsisStyles }}
        >
          {reformatDates(row?.effectiveEndDate) ?? "-"}
        </TableCell>
        <TableCell
          align="left"
          title={row?.location?.fullName}
          sx={{ ...tableCellStyle, ...synopsisStyles }}
        >
          {titleCase(row?.location?.fullName)}
        </TableCell>
        <TableCell align="left" sx={{ ...tableCellStyle, ...synopsisStyles }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip title="Edit">
              <IconButton
                onClick={() => onEdit(row?.trayId, row)}
                disabled={!props.permission}
              >
                <EditOutlinedIcon
                  color={props.permission ? "error" : "disabled"}
                ></EditOutlinedIcon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => handlClick()}
                disabled={!props.permission}
              >
                <DeleteForeverOutlinedIcon
                  color={props.permission ? "error" : "disabled"}
                ></DeleteForeverOutlinedIcon>
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow key={`parent-collapsed-row-${index}`}>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
          }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small" aria-label="collapsed emissions data">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={row?.length}
                />
                <TableBody>
                  {visibleRows?.map((collapsedRow: any) => (
                    <TableRow key={`rc-collapsed-row-${index}`}>
                      <TableCell
                        align="left"
                        sx={{
                          ...collapsedStyles,
                          ...synopsisStyles,
                          maxWidth: "100px",
                        }}
                        title={collapsedRow?.code}
                      >
                        {collapsedRow?.steel?.code}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        align="left"
                        sx={{
                          ...collapsedStyles,
                          ...synopsisStyles,
                          maxWidth: "100px",
                        }}
                        title={collapsedRow?.steel?.name}
                      >
                        {collapsedRow?.steel?.name}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...collapsedStyles,
                          ...synopsisStyles,
                          maxWidth: "100px",
                        }}
                        title={collapsedRow?.steel?.grade}
                      >
                        {collapsedRow?.steel?.grade}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...collapsedStyles,
                          ...synopsisStyles,
                          maxWidth: "100px",
                        }}
                        title={collapsedRow?.steel?.brand}
                      >
                        {collapsedRow?.steel?.brand}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          ...collapsedStyles,
                          ...synopsisStyles,
                          maxWidth: "100px",
                        }}
                        title={collapsedRow?.steel?.manufacturer}
                      >
                        {collapsedRow?.steel?.manufacturer}
                      </TableCell>
                      <TableCell align="left" sx={collapsedStyles}>
                        {collapsedRow?.steel?.thickness}
                      </TableCell>
                      <TableCell align="left" sx={collapsedStyles}>
                        {collapsedRow?.steel?.width}
                      </TableCell>
                      <TableCell align="left" sx={collapsedStyles}>
                        {collapsedRow?.steel?.length}
                      </TableCell>
                      <TableCell align="left" sx={collapsedStyles}>
                        {collapsedRow?.quantity}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Modal
        open={openDeleteModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <div className="delete-modal-container">
            <div className="button-delete-modal-container">
              <DeleteForeverOutlinedIcon
                style={{
                  fontSize: "100px",
                  color: "#d32f2f",
                }}
              />
            </div>
            <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
              Are you sure you want to delete?
            </Typography>
            <div className="button-delete-modal-container">
              <Button
                style={{ width: "90px" }}
                variant="contained"
                onClick={() => {
                  dispatch(
                    deleteSteelPerTray({
                      trayId: row?.trayId,
                      locationId: row?.locationId,
                      configId: row?._id,
                      callback: refreshSteelPerTray,
                    })
                  ).then(() => {
                    handleClose();
                  });
                }}
              >
                Confirm
              </Button>
              <Button
                style={{ width: "90px" }}
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
